import AddSpaceClusterDTO from "../../../shared/oversight-core/dtos/request-dtos/add-space-cluster-dto";
import AddSpaceDTO from "../../../shared/oversight-core/dtos/request-dtos/add-space-dto";
import UpdateSpaceClusterDTO from "../../../shared/oversight-core/dtos/request-dtos/update-space-cluster-dto";
import UpdateSpaceDTO from "../../../shared/oversight-core/dtos/request-dtos/update-space-dto";
import ViewDevicesWithAbnormalPowerConsumptionRequestDTO from "../../../shared/oversight-core/dtos/request-dtos/view-devices-with-abnormal-power-consumption-request-dto";
import CheckDevicesWithAbnormalPowerConsumptionResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/check-devices-with-abnormal-power-consumption-response-dto";
import GetSpaceClustersDTO from "../../../shared/oversight-core/dtos/response-dtos/get-space-clusters-dto";
import ListSpaceClusterResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/list-space-cluster-response-dto";
import { IScheduledSpaceClusterShallowView } from "../../../shared/oversight-core/dtos/response-dtos/scheduled-space-sluster-shallow-view-dto";
import SpaceClustersViewResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/space-clusters-view-response-dto";
import SubSpaceClusterViewResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/sub-space-cluster-view-response-dto";
import TariffResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/tariff-response-dto";
import ViewDevicesWithAbnormalPowerConsumptionResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/view-devices-with-abnormal-power-consumption-response-dto";
import { ISpaceClusterShallowHierarchyView } from "../../../shared/oversight-core/interfaces/space-hierarchy";
import { transformErrorResponse } from "../../../shared/oversight-core/utils/transformErrorResponse";
import { rootApi } from "../apiManager";

export const spaceAPI = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getSpaces: builder.query<SpaceClustersViewResponseDTO, void>({
      transformErrorResponse,
      query() {
        return {
          url: `space-cluster/view`,
          method: "GET",
        };
      },
    }),
    getSpace: builder.query<
      SubSpaceClusterViewResponseDTO,
      { clusterId: string; spaceId: string }
    >({
      transformErrorResponse,
      query({ clusterId, spaceId }) {
        return {
          url: `space-cluster/${clusterId}/sub-root/${spaceId}/view`,
          method: "GET",
        };
      },
    }),
    getSpaceForControllers: builder.query<
      SubSpaceClusterViewResponseDTO,
      { clusterId: string; spaceId: string }
    >({
      transformErrorResponse,
      query({ clusterId, spaceId }) {
        return {
          url: `space-cluster/${clusterId}/sub-root/${spaceId}/controllers/view`,
          method: "GET",
        };
      },
    }),
    addSpaceCluster: builder.mutation<
      { spaceClusterId: string; rootSpaceId: string },
      AddSpaceClusterDTO
    >({
      transformErrorResponse,
      query(data) {
        return {
          url: "space-cluster/start",
          method: "POST",
          body: data,
        };
      },
    }),
    addSpace: builder.mutation<
      AddSpaceDTO,
      {
        spaceClusterId: string | undefined;
        parentSpaceId: string | undefined;
        spaceName: string;
      }
    >({
      transformErrorResponse,
      query(data) {
        return {
          url: `space-cluster/${data.spaceClusterId}/space/add`,
          method: "POST",
          body: data,
        };
      },
    }),
    editSpaceCluster: builder.mutation<UpdateSpaceDTO, UpdateSpaceClusterDTO>({
      transformErrorResponse,
      query(data) {
        return {
          url: `space-cluster/${data.spaceClusterId}/update`,
          method: "PUT",
          body: {
            label: data.label,
            accountNumber: data.accountNumber,
          },
        };
      },
    }),
    editSpace: builder.mutation<
      UpdateSpaceDTO,
      {
        spaceClusterId: string;
        spaceId: string;
        spaceName: string;
      }
    >({
      transformErrorResponse,
      query(data) {
        return {
          url: `space-cluster/${data.spaceClusterId}/space/${data.spaceId}/update`,
          method: "PUT",
          body: { spaceName: data.spaceName },
        };
      },
    }),
    removeSpace: builder.mutation<void, { clusterId: string; spaceId: string }>(
      {
        transformErrorResponse,
        query({ clusterId, spaceId }) {
          return {
            url: `space-cluster/${clusterId}/sub-root/${spaceId}/remove`,
            method: "DELETE",
          };
        },
      }
    ),
    getSpaceClusters: builder.query<GetSpaceClustersDTO, void>({
      transformErrorResponse,
      query() {
        return {
          url: `space-cluster/list`,
          method: "GET",
        };
      },
    }),
    getSpaceClustersWithDepth: builder.query<GetSpaceClustersDTO, number>({
      transformErrorResponse,
      query(depth) {
        return {
          url: `space-cluster/list`,
          method: "GET",
          params: {
            requiredDepth: depth,
          },
        };
      },
    }),
    viewSubSpaceClusterWithSchedule: builder.query<
      { spaceCluster: IScheduledSpaceClusterShallowView },
      {
        clusterId: string;
        rootSpaceId: string;
        year: number;
        month: number;
        requiredDepth?: number;
      }
    >({
      transformErrorResponse,
      query(data) {
        return {
          url: `space-cluster/${data.clusterId}/sub-root/${data.rootSpaceId}/power-consumer-usage-schedule/list`,
          method: "GET",
          params: {
            year: data.year,
            month: data.month,
            requiredDepth: data.requiredDepth,
          },
        };
      },
    }),
    getSpaceHierarchy: builder.query<
      ISpaceClusterShallowHierarchyView,
      {
        clusterId: string;
        spaceId: string;
      }
    >({
      transformErrorResponse,
      transformResponse({
        spaceCluster,
      }: {
        spaceCluster: ISpaceClusterShallowHierarchyView;
      }) {
        const hierarchy = [...spaceCluster.ancestorSpaces].sort((a, b) => {
          if (a.parentSpaceId === null) {
            return -1;
          } else if (b.parentSpaceId === null) {
            return 1;
          } else {
            return a.id === b.parentSpaceId ? -1 : 1;
          }
        });

        return { ...spaceCluster, ancestorSpaces: hierarchy };
      },
      query(data) {
        return {
          url: `/space-cluster/${data.clusterId}/space/${data.spaceId}/hierarchy`,
          method: "GET",
        };
      },
    }),
    listSpaceCluster: builder.query<
      ListSpaceClusterResponseDTO,
      { clusterId: string }
    >({
      transformErrorResponse,
      query({ clusterId }) {
        return {
          url: `space-cluster/${clusterId}/list`,
          method: "GET",
        };
      },
    }),
    listTariffs: builder.query<TariffResponseDTO, void>({
      transformErrorResponse,
      query() {
        return {
          url: `tariff/list`,
          method: "GET",
        };
      },
    }),
    CheckDevicesWithAbnormalPowerConsumption: builder.query<
      CheckDevicesWithAbnormalPowerConsumptionResponseDTO,
      { spaceClusterId: string; spaceId?: string; requiredDepth?: number }
    >({
      transformErrorResponse,
      query({ spaceClusterId, spaceId, requiredDepth }) {
        return {
          url: `space-cluster/${spaceClusterId}/device/abnormal-energy-consumption/check`,
          method: "GET",
          params: {
            spaceId: spaceId,
            requiredDepth: requiredDepth,
          },
        };
      },
    }),
    ViewDevicesWithAbnormalPowerConsumption: builder.query<
      ViewDevicesWithAbnormalPowerConsumptionResponseDTO,
      ViewDevicesWithAbnormalPowerConsumptionRequestDTO
    >({
      transformErrorResponse,
      query(data: ViewDevicesWithAbnormalPowerConsumptionRequestDTO) {
        return {
          url: `space-cluster/${data.spaceClusterId}/device/abnormal-energy-consumption/view`,
          method: "GET",
          params: {
            spaceId: data.spaceId,
            requiredDepth: data.requiredDepth,
            pageNumber: data.pageNumber,
            pageSize: data.pageSize,
            sortBy: data.sortBy,
            ascending: data.ascending,
          },
        };
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useLazyGetSpacesQuery,
  useGetSpacesQuery,
  useLazyGetSpaceQuery,
  useLazyGetSpaceForControllersQuery,
  useRemoveSpaceMutation,
  useAddSpaceClusterMutation,
  useAddSpaceMutation,
  useEditSpaceClusterMutation,
  useEditSpaceMutation,
  useGetSpaceClustersQuery,
  useLazyGetSpaceClustersWithDepthQuery,
  useLazyViewSubSpaceClusterWithScheduleQuery,
  useLazyGetSpaceClustersQuery,
  useLazyGetSpaceHierarchyQuery,
  useLazyListSpaceClusterQuery,
  useLazyListTariffsQuery,
  useLazyCheckDevicesWithAbnormalPowerConsumptionQuery,
  useLazyViewDevicesWithAbnormalPowerConsumptionQuery,
} = spaceAPI;
